/* eslint-disable no-bitwise */
import ContainerWithShadow from 'components/common/ContainerWithShadow';
import CustomButton from 'components/common/customButton/customButton';
import Title from 'components/common/Title';
import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import plus from 'style/imgs/icons/plus.svg';
import {
  createResoureces,
  editResoureces,
  getResources,
  deleteResources,
} from 'actions/digital-resources';
import { logEvent } from 'services/amplitude';
import { Resource, IDResource } from 'types';
import { useDispatch, connect } from 'react-redux';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import buttons from 'style/buttons.module.scss';
import _ from 'lodash';
import { IRootState } from 'store';
import { useHistory } from 'react-router-dom';
import styles from './index.module.scss';
import ResourceListItem from './ResourceListItem';

function Alert(props: AlertProps) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

function createMock(
  unitId: number,
  itemId: number,
  isNewItem?: boolean
): Resource {
  return {
    id: itemId,
    link: '',
    type: null,
    ownerType: 'digital-resource',
    isNewItem,
  };
}

const mapStateToProps = (state: IRootState) => ({
  resourcesState: state.digitalResources,
  activeScholarId: state.users.userData?.activeScholarId,
  user: state.users,
});

interface IProps extends ReturnType<typeof mapStateToProps> {
  kidName: string;
}

const Resources: React.FC<IProps> = ({
  resourcesState,
  kidName,
  activeScholarId,
  user,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { units } = resourcesState;

  const [error, setError] = useState<string>('');
  const [alertOpen, changeAlertState] = useState<boolean>(false);
  const [resources, setResources] = useState<IDResource[]>(
    activeScholarId ? [createMock(activeScholarId, 0)] : []
  );

  const clearError = () => setError('');

  const savedResources = useMemo(
    () => (activeScholarId ? units[activeScholarId] : []),
    [activeScholarId, units]
  );

  useEffect(() => {
    if (activeScholarId && savedResources?.length > 0) {
      setResources(savedResources);
    }
  }, [resourcesState, activeScholarId, savedResources]);

  useEffect(() => {
    if (activeScholarId) {
      dispatch(getResources(activeScholarId));
    }
  }, [dispatch, activeScholarId]);

  const inputs = useMemo(
    () => resources.map(() => createRef<HTMLInputElement>()),
    [resources]
  );

  const tryToSubmit = useCallback(
    (r: IDResource) => {
      if (!activeScholarId || !r.link || !r.type) {
        return;
      }

      const index = savedResources?.findIndex((rs) => rs.id === r.id);
      const isCreation = index < 0 || !savedResources;

      if (isCreation) {
        dispatch(
          createResoureces(r, activeScholarId, (err: string) => {
            setError(err);
          })
        );
      } else {
        const baseResource = savedResources[index];
        const isChanged = !_.isEqual(baseResource, r);

        if (isChanged) {
          dispatch(
            editResoureces(
              r,
              activeScholarId,
              (err: string) => {
                setError(err);
              },
              () => {
                changeAlertState(true);
              }
            )
          );
        }
      }
    },
    [activeScholarId, savedResources, dispatch]
  );

  const handleAdd = useCallback(() => {
    if (!activeScholarId) {
      return;
    }
    logEvent('AddResource');
    const isEmpty = !resources || resources.length === 0;

    if (isEmpty) {
      setResources([createMock(activeScholarId, 0)]);
      return;
    }

    const reversed = [...resources].reverse();
    const lastCreated = reversed.find(
      (r) => r.ownerType === 'digital-resource'
    );

    if (!lastCreated) {
      setResources([createMock(activeScholarId, 0, true), ...resources]);
      return;
    }

    if (
      lastCreated?.link?.length &&
      resources[0].type &&
      resources[0].link.length
    ) {
      setResources((r) => {
        const newItem = [
          createMock(+activeScholarId, lastCreated.id + 1, true),
          ...r,
        ];
        return newItem;
      });
    } else {
      // focusLastInput();
    }
  }, [resources, activeScholarId]);

  const handleDelete = useCallback(
    (id: number) => {
      if (activeScholarId) {
        const isNewItem = savedResources.findIndex((r) => r.id === id) === -1;

        if (isNewItem) {
          // delete it from component state
          setResources(resources.filter((r) => r.id !== id));
        } else {
          // delete it from DB
          dispatch(deleteResources(activeScholarId, id));
        }
      }

      setResources((prevState) => prevState.filter((r) => r.id !== id));
    },
    [dispatch, activeScholarId, resources, savedResources]
  );

  const handleChange = useCallback(
    (id: number, link: string) => {
      setResources((prevState) => {
        clearError();

        const index = prevState.findIndex((r) => r.id === id);
        if (~index) {
          const newResources = [...prevState];
          const updated = {
            ...prevState[index],
            link,
          };

          newResources.splice(index, 1, updated);
          tryToSubmit(updated);
          return newResources;
        }
        return prevState;
      });
    },
    [tryToSubmit]
  );

  const handleCategoryChange = useCallback(
    (id: number, type: string) => {
      setResources((prevState) => {
        clearError();

        const index = prevState.findIndex((r) => r.id === id);
        if (~index) {
          const newResources = [...prevState];
          const updated = {
            ...prevState[index],
            type,
            ownerType: 'digital-resource',
          };
          newResources.splice(index, 1, updated);
          tryToSubmit(updated);
          return newResources;
        }
        return prevState;
      });
    },
    [tryToSubmit]
  );

  const onClose = () => {
    changeAlertState(false);
  };

  return (
    <div className={styles.container}>
      <Snackbar open={alertOpen} autoHideDuration={3000} onClose={onClose}>
        <Alert onClose={onClose} severity="success">
          Done!
        </Alert>
      </Snackbar>
      <ContainerWithShadow className={`${styles.root} ${styles.hidden}`}>
        <Title className={styles.title}>
          Build {kidName} Digital Resource Homeroom Map
        </Title>
        <div className={styles.addNew}>
          <CustomButton
            color="mint"
            text="Add Resource"
            icon={plus}
            onClick={handleAdd}
          />
        </div>
        <header className={styles.listHeader}>
          <Title className={styles.linksHeader} variant="h4">
            Website Or Digital Address
          </Title>
          <Title className={styles.categoryHeader} variant="h4">
            Category
          </Title>
        </header>
        <div className={styles.contentContainer}>
          {resources.map((r, i) => (
            <ResourceListItem
              ref={inputs[i]}
              key={r.id || r.link}
              resource={r}
              onDelete={handleDelete}
              onChange={handleChange}
              onCategoryChange={handleCategoryChange}
            />
          ))}
        </div>
      </ContainerWithShadow>
      <div className={styles.buttons}>
        {error && (
          <span className={`${styles.errorMessage} error-message`}>
            {error}
          </span>
        )}
        <button
          onClick={() => history.push('/onboarding/timetable')}
          className={buttons.buttonOutline}
          type="button"
        >
          Back
        </button>
        <button
          type="submit"
          className={buttons.buttomPrimary}
          onClick={() => {
            if (user.userData?.type === 'parent') {
              history.push('/onboarding/timetable/signin-with-credentials');
            } else {
              history.push('/сongratulations');
            }
            logEvent('HomeroomNext');
          }}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default connect(mapStateToProps)(Resources);
